import { Button } from "@beweegbaas/ui";
import { ClientOnly, createFileRoute, Outlet } from "@tanstack/react-router";
import { Page } from "#/components/page";
import { getTeacherLesResultaten } from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import { useDownloadMediakaartenForLes } from "#/modules/mediakaarten/download-mediakaarten-reeks";
import { MediakaartWithPhotosPdfPreview } from "#/modules/mediakaarten/pdf-components/pdf.mediakaart-with-photos.previewer.client";
import { MediakaartPdfPreview } from "#/modules/mediakaarten/pdf-components/pdf.mediakaart.previewer.client";
import { useDocentLes } from "#/modules/les.docent-gestuurd/use-docent-les";
import { buildLocaleHref } from "#/modules/util.helpers/route-href";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher/mediakaarten",
)({
  loader: ({ params }) => getTeacherLesResultaten({ data: { lesId: params.lesson } }),
  component: TeacherLessonMediakaarten,
});

function TeacherLessonMediakaarten() {
  const { resultaten } = Route.useLoaderData();
  const { locale, schoolyear, school } = Route.useParams();
  const { lesson } = useDocentLes();
  const downloadMutation = useDownloadMediakaartenForLes();

  const beweegactiviteiten = resultaten?.beweegactiviteiten ?? [];
  const sociaalEmotioneelThemas = resultaten?.sociaalEmotioneelThemas ?? [];
  const hasCards = beweegactiviteiten.length > 0 || sociaalEmotioneelThemas.length > 0;

  const resourceBaseUrl = import.meta.env.SSR
    ? ""
    : `${window.location.origin}${buildLocaleHref(`/api/${school}/resource`, { locale, schoolyear })}`;

  return (
    <Page.Container>
      {hasCards ? (
        <div className="flex flex-col gap-10">
          <div className="flex items-center justify-between gap-4">
            <h2 className="m-0 text-lg font-semibold">Mediakaarten</h2>
            <Button
              className="shrink-0"
              isPending={downloadMutation.isPending}
              onClick={() =>
                downloadMutation.mutate({
                  beweegactiviteiten,
                  sociaalEmotioneelThemas,
                  resourceBaseUrl,
                  zipName: lesson.klasName ?? "les",
                })
              }
            >
              <IconMaterialSymbolsDownload className="size-4" />
              Download
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            {beweegactiviteiten.map((activiteit) => (
              <ClientOnly
                key={activiteit.id}
                fallback={<div className="bg-muted aspect-297/210 w-full animate-pulse rounded" />}
              >
                <MediakaartPdfPreview cardData={activiteit} />
              </ClientOnly>
            ))}
            {sociaalEmotioneelThemas.map((thema) => (
              <ClientOnly
                key={thema.id}
                fallback={<div className="bg-muted aspect-297/210 w-full animate-pulse rounded" />}
              >
                <MediakaartPdfPreview cardData={thema} />
              </ClientOnly>
            ))}
          </div>
          {beweegactiviteiten.length > 0 && (
            <div>
              <h2 className="mb-4 text-lg font-semibold">Mediakaarten met foto&apos;s</h2>
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                {beweegactiviteiten.map((activiteit) => (
                  <ClientOnly
                    key={activiteit.id}
                    fallback={
                      <div className="bg-muted aspect-297/420 w-full animate-pulse rounded" />
                    }
                  >
                    <MediakaartWithPhotosPdfPreview
                      cardData={activiteit}
                      resourceBaseUrl={resourceBaseUrl}
                    />
                  </ClientOnly>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-muted-foreground">Deze les heeft geen mediakaarten om te tonen.</p>
      )}
      <Outlet />
    </Page.Container>
  );
}
